import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';

interface User {
  id: number;
  name: string;
  relationship: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  audioUrl?: string;
  debug?: {
    duration_ms: number;
    memories_used: string[];
    prompts_applied: string[];
    tokens_used?: number;
  };
}

export default function Chat() {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDebug, setShowDebug] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchUsers = async () => {
    try {
      const res = await apiFetch('/api/chat/users');
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
        if (data.length > 0) {
          setSelectedUser(data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !selectedUser || loading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await apiFetch('/api/chat/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: input,
          simulate_user_id: selectedUser
        })
      });

      if (res.ok) {
        const data = await res.json();
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
          audioUrl: data.audio_url,
          debug: data.debug_info
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const playAudio = (url: string) => {
    const audio = new Audio(url);
    audio.play();
  };

  const clearChat = () => {
    setMessages([]);
  };

  const selectedUserInfo = users.find(u => u.id === selectedUser);

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col animate-fadeUp">
      {/* 頂部控制列 */}
      <div
        className="flex items-center justify-between p-4 rounded-t-2xl"
        style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border)' }}
      >
        <div className="flex items-center gap-4">
          {/* 身份選擇器 */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-text-secondary">模擬身份</span>
            <select
              value={selectedUser || ''}
              onChange={(e) => setSelectedUser(parseInt(e.target.value))}
              className="px-3 py-2 rounded-lg text-sm"
              style={{
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border)',
                color: 'var(--text-primary)',
              }}
            >
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* 清除按鈕 */}
          {messages.length > 0 && (
            <button
              onClick={clearChat}
              className="px-3 py-2 rounded-lg text-sm transition-all"
              style={{
                background: 'var(--bg-surface-2)',
                border: '1px solid var(--border)',
                color: 'var(--text-muted)',
              }}
            >
              清除對話
            </button>
          )}
        </div>

        {/* Debug 開關 */}
        <button
          onClick={() => setShowDebug(!showDebug)}
          className="px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2"
          style={{
            background: showDebug ? 'rgba(0, 212, 255, 0.1)' : 'var(--bg-surface-2)',
            border: `1px solid ${showDebug ? 'rgba(0, 212, 255, 0.3)' : 'var(--border)'}`,
            color: showDebug ? '#00d4ff' : 'var(--text-secondary)',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 19V5M5 12l7-7 7 7"/>
          </svg>
          Debug
        </button>
      </div>

      {/* 聊天區域 */}
      <div
        className="flex-1 overflow-hidden flex"
        style={{ background: 'var(--bg-surface)' }}
      >
        {/* 訊息列表 */}
        <div className={`flex-1 overflow-y-auto p-4 ${showDebug ? 'w-2/3' : 'w-full'}`}>
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              {/* 呼吸光球 */}
              <div className="relative w-24 h-24 mb-6">
                <div
                  className="absolute inset-0 rounded-full animate-breathe"
                  style={{ background: 'radial-gradient(circle, rgba(233, 30, 140, 0.3) 0%, transparent 70%)' }}
                />
                <div
                  className="absolute inset-4 rounded-full animate-breathe"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, #ff6bb3, #e91e8c)',
                    boxShadow: '0 0 40px rgba(233, 30, 140, 0.4)',
                    animationDelay: '0.2s',
                  }}
                />
                <div
                  className="absolute w-3 h-3 rounded-full bg-white/70"
                  style={{ top: '26%', left: '30%', filter: 'blur(1px)' }}
                />
              </div>
              <p className="text-text-secondary text-lg mb-2">
                嗨～有什麼想跟我說的嗎？
              </p>
              <p className="text-text-muted text-sm">
                目前以 <span style={{ color: '#e91e8c' }}>{selectedUserInfo?.name}</span> 的身份聊天
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fadeUp`}
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  <div className={`max-w-[75%] ${msg.role === 'assistant' ? 'flex gap-3' : ''}`}>
                    {/* AI 頭像 */}
                    {msg.role === 'assistant' && (
                      <div
                        className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
                        style={{
                          background: 'linear-gradient(135deg, rgba(233, 30, 140, 0.2), rgba(233, 30, 140, 0.05))',
                        }}
                      >
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{
                            background: 'radial-gradient(circle at 30% 30%, #ff6bb3, #e91e8c)',
                            boxShadow: '0 0 10px rgba(233, 30, 140, 0.4)',
                          }}
                        />
                      </div>
                    )}

                    <div>
                      {/* 訊息氣泡 */}
                      <div
                        className={`message-bubble ${msg.role}`}
                        style={{
                          background: msg.role === 'user'
                            ? 'linear-gradient(135deg, var(--pink), var(--pink-bright))'
                            : 'var(--bg-surface-2)',
                          border: msg.role === 'assistant' ? '1px solid var(--border)' : 'none',
                        }}
                      >
                        {msg.content}
                      </div>

                      {/* 時間和語音按鈕 */}
                      <div className={`flex items-center gap-2 mt-1 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        <span className="text-xs text-text-muted">
                          {msg.timestamp.toLocaleTimeString('zh-TW', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                        {msg.role === 'user' && (
                          <span className="text-xs text-text-muted">· {selectedUserInfo?.name}</span>
                        )}
                        {msg.audioUrl && (
                          <button
                            onClick={() => playAudio(msg.audioUrl!)}
                            className="p-1 rounded hover:bg-pink/10 transition-colors"
                            style={{ color: '#e91e8c' }}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* 載入中 */}
              {loading && (
                <div className="flex justify-start animate-fadeUp">
                  <div className="flex gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center"
                      style={{
                        background: 'linear-gradient(135deg, rgba(233, 30, 140, 0.2), rgba(233, 30, 140, 0.05))',
                      }}
                    >
                      <div
                        className="w-4 h-4 rounded-full animate-breathe"
                        style={{
                          background: 'radial-gradient(circle at 30% 30%, #ff6bb3, #e91e8c)',
                          boxShadow: '0 0 10px rgba(233, 30, 140, 0.4)',
                        }}
                      />
                    </div>
                    <div
                      className="px-4 py-3 rounded-2xl rounded-bl-sm"
                      style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border)' }}
                    >
                      <div className="flex gap-1">
                        <span className="w-2 h-2 rounded-full bg-pink animate-bounce" style={{ animationDelay: '0s' }}></span>
                        <span className="w-2 h-2 rounded-full bg-pink animate-bounce" style={{ animationDelay: '0.15s' }}></span>
                        <span className="w-2 h-2 rounded-full bg-pink animate-bounce" style={{ animationDelay: '0.3s' }}></span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Debug 面板 */}
        {showDebug && (
          <div
            className="w-1/3 border-l overflow-y-auto p-4"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-surface-2)' }}
          >
            <h3 className="text-sm font-medium text-text-secondary mb-4 flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 16v-4M12 8h.01"/>
              </svg>
              Debug 資訊
            </h3>

            {messages.filter(m => m.role === 'assistant' && m.debug).length === 0 ? (
              <p className="text-text-muted text-sm">
                送出訊息後會顯示 debug 資訊
              </p>
            ) : (
              <div className="space-y-4">
                {messages.filter(m => m.role === 'assistant' && m.debug).map(msg => (
                  <div
                    key={msg.id}
                    className="p-3 rounded-xl text-xs"
                    style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
                  >
                    <div className="text-text-muted mb-2">
                      {msg.timestamp.toLocaleTimeString()}
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span style={{ color: '#00d4ff' }}>⏱️</span>
                        <span className="text-text-secondary">
                          回應時間: <span className="font-mono text-text-primary">{msg.debug?.duration_ms}ms</span>
                        </span>
                      </div>

                      {msg.debug?.tokens_used && (
                        <div className="flex items-center gap-2">
                          <span style={{ color: '#a855f7' }}>🔢</span>
                          <span className="text-text-secondary">
                            Token: <span className="font-mono text-text-primary">{msg.debug.tokens_used}</span>
                          </span>
                        </div>
                      )}

                      <div className="flex items-start gap-2">
                        <span style={{ color: '#e91e8c' }}>📝</span>
                        <div className="text-text-secondary">
                          <div>已套用 Prompt:</div>
                          <div className="mt-1 space-y-1">
                            {msg.debug?.prompts_applied?.map((p, i) => (
                              <div
                                key={i}
                                className="px-2 py-1 rounded"
                                style={{ background: 'rgba(233, 30, 140, 0.1)', color: '#f472b6' }}
                              >
                                {p}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-start gap-2">
                        <span style={{ color: '#00e676' }}>💭</span>
                        <div className="text-text-secondary">
                          <div>使用記憶: <span className="font-mono text-text-primary">{msg.debug?.memories_used?.length || 0}</span> 筆</div>
                          {msg.debug?.memories_used && msg.debug.memories_used.length > 0 && (
                            <div className="mt-1 space-y-1">
                              {msg.debug.memories_used.slice(0, 3).map((m, i) => (
                                <div
                                  key={i}
                                  className="px-2 py-1 rounded text-text-muted truncate"
                                  style={{ background: 'rgba(0, 230, 118, 0.05)' }}
                                >
                                  {m}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 輸入區域 */}
      <div
        className="p-4 rounded-b-2xl"
        style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border)' }}
      >
        <div className="flex gap-3">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="輸入訊息..."
            disabled={loading}
            className="flex-1 h-12 px-4 rounded-xl text-sm"
            style={{
              background: 'var(--bg-surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text-primary)',
            }}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="h-12 px-6 rounded-xl font-medium text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{
              background: 'linear-gradient(135deg, var(--pink), var(--pink-bright))',
              boxShadow: '0 4px 20px rgba(233, 30, 140, 0.3)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
            </svg>
            送出
          </button>
        </div>
      </div>
    </div>
  );
}
